import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { JobCard } from "@/components/JobCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, Search, MapPin } from "lucide-react";
import { formatSalaryDutch } from "@/lib/formatSalary";
import { useBoostedJobIds } from "@/hooks/useBoostedJobIds";
import { getJobBranding } from "@/lib/jobBranding";


const TARGET = 6;

// Mulberry32 deterministic PRNG seeded by day
const seededShuffle = <T,>(arr: T[], seed: number): T[] => {
  const a = [...arr];
  let s = seed || 1;
  const rand = () => {
    s |= 0; s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const HomepageJobsSection = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [locationTerm, setLocationTerm] = useState("");

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set("search", searchTerm.trim());
    if (locationTerm.trim()) params.set("locatie", locationTerm.trim());
    const qs = params.toString();
    navigate(qs ? `/vacatures?${qs}` : "/vacatures");
  };

  const { data: boosted } = useBoostedJobIds();
  const boostedIds = boosted?.ids ?? new Set<string>();

  const { data: boostedJobs = [] } = useQuery({
    queryKey: ["homepage-boosted-jobs", boostedIds.size],
    enabled: boostedIds.size > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const ids = Array.from(boostedIds);
      const { data, error } = await supabase
        .from("jobs")
        .select(`
          id, slug, title, description, location, salary, salary_min, salary_max, hours, hours_min, hours_max,
          tags, employment_type, banner_url, company_id, created_at, is_active,
          companies ( name, logo_url, is_premium, cover_url ),
          sub_label:company_sub_labels!jobs_sub_label_id_fkey ( id, name, logo_url, is_active )
        `)
        .in("id", ids)
        .eq("is_active", true);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: featured, isSuccess: featuredLoaded } = useQuery({
    queryKey: ['homepage-featured-jobs-manual'],
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('homepage_featured_jobs')
        .select(`
          sort_order,
          jobs (
            id, slug, title, description, location, salary, salary_min, salary_max, hours, hours_min, hours_max,
            tags, employment_type, banner_url, company_id, created_at, is_active,
            companies ( name, logo_url, is_premium, cover_url ),
            sub_label:company_sub_labels!jobs_sub_label_id_fkey ( id, name, logo_url, is_active )
          )
        `)
        .order('sort_order');
      if (error) throw error;
      return (data ?? [])
        .map((r: any) => r.jobs)
        .filter((j: any) => j && j.is_active);
    },
  });

  const useFallback = featuredLoaded && (!featured || featured.length === 0);

  const { data: jobs, isLoading } = useQuery({
    queryKey: ['homepage-featured-jobs'],
    enabled: useFallback,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id, slug, title, description, location, salary, salary_min, salary_max, hours, hours_min, hours_max,
          tags, employment_type, banner_url, company_id, created_at,
          companies ( name, logo_url, is_premium, cover_url ),
          sub_label:company_sub_labels!jobs_sub_label_id_fkey ( id, name, logo_url, is_active )
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      return data ?? [];
    },
  });

  const selected = useMemo(() => {
    // Daily seed voor deterministische roulatie
    const today = new Date();
    const seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();

    // Boosted jobs krijgen altijd voorrang, geshuffled per dag
    const boostedList = seededShuffle((boostedJobs ?? []).filter((j: any) => j && j.is_active), seed);

    // Base pool: manual featured of fallback op nieuwste vacatures
    const basePool: any[] = useFallback
      ? (jobs ?? [])
      : ((featured ?? []) as any[]);

    // Filter boosted uit basePool zodat we niet dubbel tonen
    const boostedSet = new Set(boostedList.map((j: any) => j.id));
    const nonBoosted = basePool.filter((j: any) => j && !boostedSet.has(j.id));

    if (!useFallback) {
      // Manual featured: eerst geboost, dan de handmatige selectie
      return [...boostedList, ...nonBoosted].slice(0, TARGET);
    }

    if (nonBoosted.length === 0 && boostedList.length === 0) return [];

    // Group non-boosted by company voor spreiding
    const byCompany = new Map<string, any[]>();
    for (const j of nonBoosted) {
      const key = j.company_id || "unknown";
      if (!byCompany.has(key)) byCompany.set(key, []);
      byCompany.get(key)!.push(j);
    }

    const companyKeys = seededShuffle(Array.from(byCompany.keys()), seed);

    const result: any[] = [...boostedList];
    // Pass 1: 1 per company
    for (const key of companyKeys) {
      if (result.length >= TARGET) break;
      result.push(byCompany.get(key)![0]);
    }
    // Pass 2: fill with second job per company (max 2)
    if (result.length < TARGET) {
      for (const key of companyKeys) {
        if (result.length >= TARGET) break;
        const second = byCompany.get(key)![1];
        if (second) result.push(second);
      }
    }
    return result.slice(0, TARGET);
  }, [jobs, featured, useFallback, boostedJobs]);

  const formatHours = (j: any) => {
    if (j.hours_min) {
      if (j.hours_max && j.hours_max !== j.hours_min) return `${j.hours_min}-${j.hours_max} uur`;
      return `${j.hours_min} uur`;
    }
    return j.hours || '';
  };

  const formatSalary = (j: any): string => {
    if (j.salary && String(j.salary).trim()) return j.salary;
    const min = j.salary_min ? Number(j.salary_min) : null;
    const max = j.salary_max ? Number(j.salary_max) : null;
    if (min && max && min !== max) return `€${formatSalaryDutch(min)} - €${formatSalaryDutch(max)}`;
    if (min) return `Vanaf €${formatSalaryDutch(min)}`;
    if (max) return `Tot €${formatSalaryDutch(max)}`;
    return '';
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 gradient-cyan">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-black mb-3 text-center">
            Werkgevers zoeken jou
          </h2>
          <p className="text-base md:text-lg text-muted-foreground text-center">
            Of jij zoekt hen — scroll door de nieuwste vacatures
          </p>
        </div>

        {/* Grote zoekbalk — zelfde stijl als /vacatures */}
        <div className="w-full mb-10 md:mb-12">
          <Card className="bg-card border border-border shadow-none rounded-xl sm:rounded-2xl overflow-visible">
            <CardContent className="p-2 sm:p-3">
              <form onSubmit={handleSearchSubmit} className="flex flex-col gap-2 md:grid md:grid-cols-[minmax(0,1.85fr)_minmax(0,1fr)_auto] md:items-stretch">
                <div className="flex items-center gap-3 bg-muted rounded-xl sm:rounded-2xl px-3 sm:px-5 py-1.5 sm:py-2 transition-all focus-within:ring-2 focus-within:ring-cyan">
                  <Search className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-dark flex-shrink-0" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Waar ben je naar op zoek?"
                    aria-label="Functie of trefwoord"
                    className="flex-1 h-10 sm:h-12 text-base sm:text-lg font-semibold border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-foreground/40 px-0"
                  />
                </div>
                <div className="min-w-0 flex items-center gap-2 bg-muted rounded-xl sm:rounded-2xl px-3 sm:px-4 py-1.5 sm:py-2 transition-all focus-within:ring-2 focus-within:ring-cyan">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-dark flex-shrink-0" />
                  <Input
                    value={locationTerm}
                    onChange={(e) => setLocationTerm(e.target.value)}
                    placeholder="Plaats of postcode"
                    aria-label="Plaats of postcode"
                    className="flex-1 min-w-0 h-10 sm:h-12 text-base font-medium border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-foreground/40 px-0"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="h-12 sm:h-auto px-6 sm:px-8 rounded-xl sm:rounded-2xl font-bold text-base bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-none"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Zoeken
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {(!featuredLoaded || (useFallback && isLoading))
            ? Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-[480px] rounded-3xl" />
              ))
            : selected.map((job: any) => {
                const branding = getJobBranding({ sub_label: job.sub_label, company: job.companies });
                return (
                <JobCard
                  key={job.id}
                  id={job.id}
                  slug={job.slug}
                  title={job.title}
                  description={job.description}
                  company={branding.name || 'Bedrijf'}
                  location={job.location || ''}
                  hours={formatHours(job)}
                  salary={formatSalary(job)}
                  tags={job.tags || []}
                  coverImage={job.banner_url || (job.companies?.is_premium ? job.companies?.cover_url : undefined)}
                  companyLogo={branding.logoUrl || undefined}
                  isPremium={!!job.companies?.is_premium}
                  employmentType={job.employment_type}
                />
                );
              })}
        </div>

        <div className="mt-10 md:mt-12 text-center max-w-2xl mx-auto">
          <p className="text-base md:text-lg text-foreground/80 mb-6">
            Niet alle werkgevers plaatsen vacatures — velen benaderen jou direct via je profiel. 👀
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              variant="cta"
              size="lg"
              className="group"
              onClick={() => navigate('/signup/kandidaat?src=vacatures')}
            >
              Zet jezelf op de radar
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="font-semibold"
              onClick={() => navigate('/vacatures')}
            >
              Bekijk alle vacatures
              <ArrowRight className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
